import React, { useEffect, useId, useRef, useState } from "react";
import { MdInfoOutline } from "react-icons/md";
import "../../Styles/InfoHint.css";

/* --------------------------------------------------------------------------
   InfoHint — a small "i" affordance that opens a plain-English popover
   explaining a chart or KPI card: what it shows, how to read it, and what
   to use it for. Fully keyboard accessible; closes on outside click / Esc.

   `info` shape:  { title?: string, sections: [{ h: string, b: string }] }
   -------------------------------------------------------------------------- */

function InfoHint({ info, className = "", align = "right", onOpenChange, trigger = "click" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const prevOpen = useRef(open);
  const panelId = useId();

  useEffect(() => {
    if (prevOpen.current === open) return;
    prevOpen.current = open;
    if (typeof onOpenChange === "function") onOpenChange(open);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!info) return null;

  const sections = Array.isArray(info) ? info : info.sections || [];
  const title = Array.isArray(info) ? null : info.title;
  if (!sections.length) return null;

  const stop = (e) => e.stopPropagation();

  const handleMouseEnter = () => {
    if (trigger === "hover") {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === "hover") {
      setOpen(false);
    }
  };

  const isRelative = className.includes("hc-infohint--chart") || className.includes("position-relative") || className.includes("position-static");
  const hasCustomPos = className.includes("hc-infohint--kpi") || className.includes("hc-infohint--chart");
  const posClasses = isRelative ? "" : `position-absolute ${hasCustomPos ? "" : (align === "left" ? "start-0" : "end-0")}`;

  return (
    <span
      className={`hc-infohint ${posClasses} lh-1 ${className}`.trim()}
      data-align={align}
      ref={wrapRef}
      onClick={stop}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="hc-infohint-btn btn p-0 d-inline-flex align-items-center justify-content-center rounded-circle border shadow-sm"
        aria-label={title ? `About this: ${title}` : "What this shows"}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={(e) => {
          e.stopPropagation();
          if (trigger !== "hover") {
            setOpen((o) => !o);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") e.stopPropagation();
        }}
        onFocus={() => {
          if (trigger === "hover") {
            setOpen(true);
          }
        }}
        onBlur={() => {
          if (trigger === "hover") {
            setOpen(false);
          }
        }}
      >
        <MdInfoOutline size={15} aria-hidden="true" />
      </button>

      {open ? (
        <span
          className={`hc-infohint-pop position-absolute bg-white border rounded-4 shadow-lg p-3 text-start d-flex flex-column gap-3 ${align === "left" ? "start-0" : "end-0"}`}
          id={panelId}
          role="dialog"
          aria-label={title || "About this"}
        >
          {title ? <span className="hc-infohint-pop-title fw-bold text-dark border-bottom pb-2">{title}</span> : null}
          {sections.map((s, i) => (
            <span className="hc-infohint-row d-flex flex-column gap-1" key={i}>
              <span className="hc-infohint-row-h text-uppercase fw-bold">{s.h}</span>
              <span className="hc-infohint-row-b lh-base">{s.b}</span>
            </span>
          ))}
        </span>
      ) : null}
    </span>
  );
}

export default InfoHint;
